import React from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale
} from 'chart.js';
import { Scatter } from 'react-chartjs-2';
import moment from 'moment';
import 'chartjs-adapter-moment';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    TimeScale,
    Title,
    Tooltip,
    Legend
);

const PresenceChart = ({ sleepData, presenceData, secondaryPresenceData, startTime, endTime, sleepTransitions, visitTransitions }) => {
    // Transform the data for scatter plot format
    // We'll convert the data to show dots only when the state is true (y=1)
    const transformToScatterFormat = (sourceData, yValue) => {
        if (!sourceData || !sourceData.length) return [];
        return sourceData
            .filter(point => point.y === 1) // Only include points where state is true
            .map(point => ({
                x: point.t,
                y: yValue // Fixed y-value based on category (Sleep=1, Visit=0)
            }));
    };

    const sleepScatterData = transformToScatterFormat(sleepData, 1);
    const visitScatterData = transformToScatterFormat(presenceData, 0);

    // Common chart configuration options
    const baseOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
            mode: 'index',
            axis: 'x'
        },
        hover: {
            mode: 'nearest',
            intersect: false
        },
        plugins: {
            legend: {
                display: true,
                position: 'top',
                onHover: (event, legendItem, legend) => {
                    const tooltips = {
                        'Sleep State': 'Periods when the patient is detected to be sleeping',
                        'Present': 'Periods when the patient is detected in bed',
                        'Secondary Presence': 'Periods when additional presence is detected'
                    };
                    
                    // Change cursor to indicate interactivity
                    event.native.target.style.cursor = 'pointer';
                    
                    // Find tooltip container or create it if it doesn't exist
                    let tooltipEl = document.getElementById('chart-legend-tooltip');
                    if (!tooltipEl) {
                        tooltipEl = document.createElement('div');
                        tooltipEl.id = 'chart-legend-tooltip';
                        tooltipEl.style.position = 'absolute';
                        tooltipEl.style.background = 'rgba(0,0,0,0.7)';
                        tooltipEl.style.color = 'white';
                        tooltipEl.style.padding = '5px 10px';
                        tooltipEl.style.borderRadius = '3px';
                        tooltipEl.style.fontSize = '12px';
                        tooltipEl.style.pointerEvents = 'none';
                        tooltipEl.style.opacity = '0';
                        tooltipEl.style.transition = 'opacity 0.2s';
                        tooltipEl.style.zIndex = '10';
                        document.body.appendChild(tooltipEl);
                    }
                    
                    // Show tooltip with description
                    if (tooltips[legendItem.text]) {
                        tooltipEl.innerHTML = tooltips[legendItem.text];
                        tooltipEl.style.opacity = '1';
                        tooltipEl.style.left = event.native.pageX + 'px';
                        tooltipEl.style.top = (event.native.pageY - 28) + 'px'; // Position above the cursor
                    }
                },
                onLeave: (event, legendItem, legend) => {
                    // Reset cursor
                    if (event.native && event.native.target) {
                        event.native.target.style.cursor = 'default';
                    }
                    
                    // Hide tooltip
                    const tooltipEl = document.getElementById('chart-legend-tooltip');
                    if (tooltipEl) {
                        tooltipEl.style.opacity = '0';
                    }
                }
            },
            tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false,
                callbacks: {
                    title: function(context) {
                        if (!context.length) return '';
                        return moment(context[0].parsed.x).format('MM/DD h:mma');
                    },
                    label: function(context) {
                        if (context.dataset.label === 'Present') {
                            return context.parsed.y === 1 ? 'Patient Present' : 'Patient Not Present';
                        }
                        // Hide tooltips for all other datasets
                        return false;
                    }
                }
            },
            datalabels: {
                display: false
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    unit: 'hour',
                    displayFormats: {
                        hour: 'MM/DD h:mma'
                    }
                },
                min: startTime,
                max: endTime,
                title: {
                    display: true,
                    text: 'Time'
                },
                ticks: {
                    maxRotation: 0,
                    autoSkip: true,
                    padding: 8,
                    align: 'center'
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',
                    tickLength: 8
                },
                border: {
                    display: true
                },
                afterFit: function(scale) {
                    // Ensure consistent padding/margin across charts
                    scale.paddingRight = 15;
                    scale.paddingLeft = 15;
                }
            }
        }
    };

    // Sleep chart specific options
    const sleepChartOptions = {
        ...baseOptions,
        plugins: {
            ...baseOptions.plugins,
            title: {
                display: true,
                text: 'Sleep State',
                font: { size: 16 }
            }
        },
        scales: {
            ...baseOptions.scales,
            y: {
                type: 'linear',
                min: 0.5,
                max: 1.5,
                ticks: {
                    stepSize: 1,
                    callback: (value) => {
                        if (value === 1) return 'Sleep';
                        return '';
                    }
                },
                title: {
                    display: true,
                    text: 'State'
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)'
                }
            }
        }
    };

    // Visit chart specific options
    const visitChartOptions = {
        ...baseOptions,
        plugins: {
            ...baseOptions.plugins,
            title: {
                display: true,
                text: 'Secondary Presence',
                font: { size: 16 }
            }
        },
        scales: {
            ...baseOptions.scales,
            y: {
                type: 'linear',
                min: -0.5,
                max: 0.5,
                ticks: {
                    stepSize: 1,
                    callback: (value) => {
                        if (value === 0) return 'Vist';
                        return '';
                    }
                },
                title: {
                    display: true,
                    text: 'State'
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)'
                }
            }
        }
    };

    // Print detailed data in the console for presence chart
    console.log('PresenceChart Data:', {
        timeRange: {
            startTime: new Date(startTime).toISOString(),
            endTime: new Date(endTime).toISOString(),
            durationHours: moment(endTime).diff(moment(startTime), 'hours', true).toFixed(2)
        },
        sleepData: {
            totalPoints: sleepData?.length || 0,
            scatterPoints: sleepScatterData.length,
            timeSpan: sleepData?.length ? {
                firstPoint: new Date(sleepData[0]?.t).toISOString(),
                lastPoint: new Date(sleepData[sleepData.length - 1]?.t).toISOString()
            } : null,
            transitionCounts: sleepTransitions?.length || 0,
            transitionSamples: sleepTransitions?.slice(0, 3),
            samplePoints: sleepData?.slice(0, 5)
        },
        presenceData: {
            totalPoints: presenceData?.length || 0,
            timeSpan: presenceData?.length ? {
                firstPoint: new Date(presenceData[0]?.t).toISOString(),
                lastPoint: new Date(presenceData[presenceData.length - 1]?.t).toISOString()
            } : null,
            stateDistribution: presenceData?.length ? {
                presentCount: presenceData.filter(d => d.y === 1).length,
                absentCount: presenceData.filter(d => d.y === 0).length,
                presentPercentage: ((presenceData.filter(d => d.y === 1).length / presenceData.length) * 100).toFixed(2) + '%'
            } : null,
            transitionCounts: visitTransitions?.length || 0,
            transitionSamples: visitTransitions?.slice(0, 3),
            samplePoints: presenceData?.slice(0, 5)
        },
        secondaryPresenceData: {
            totalPoints: secondaryPresenceData?.length || 0,
            timeSpan: secondaryPresenceData?.length ? {
                firstPoint: new Date(secondaryPresenceData[0]?.t).toISOString(),
                lastPoint: new Date(secondaryPresenceData[secondaryPresenceData.length - 1]?.t).toISOString()
            } : null,
            stateDistribution: secondaryPresenceData?.length ? {
                presentCount: secondaryPresenceData.filter(d => d.y === 1).length,
                absentCount: secondaryPresenceData.filter(d => d.y === 0).length,
                presentPercentage: ((secondaryPresenceData.filter(d => d.y === 1).length / secondaryPresenceData.length) * 100).toFixed(2) + '%'
            } : null,
            samplePoints: secondaryPresenceData?.slice(0, 5)
        }
    });

    // Calculate sleep statistics
    const sleepStats = sleepData?.length ? {
        totalSleepPoints: sleepData.filter(d => d.y === 1).length,
        sleepPercentage: ((sleepData.filter(d => d.y === 1).length / sleepData.length) * 100).toFixed(1),
        longestSleepSegment: calculateLongestSegment(sleepData, 1),
        sleepTransitions: sleepTransitions?.length || 0
    } : null;

    // Calculate visit presence statistics
    const visitStats = presenceData?.length ? {
        totalPresencePoints: presenceData.filter(d => d.y === 1).length,
        presencePercentage: ((presenceData.filter(d => d.y === 1).length / presenceData.length) * 100).toFixed(1),
        longestPresenceSegment: calculateLongestSegment(presenceData, 1)
    } : null;

    // Helper function to calculate longest segment duration
    function calculateLongestSegment(data, targetValue) {
        if (!data || !data.length) return 0;
        
        let currentStreak = 0;
        let maxStreak = 0;
        let lastTimestamp = null;
        
        for (let i = 0; i < data.length; i++) {
            const point = data[i];
            
            if (point.y === targetValue) {
                if (lastTimestamp && point.t - lastTimestamp <= 60000) { // Within 1 minute
                    currentStreak++;
                } else {
                    currentStreak = 1;
                }
                
                maxStreak = Math.max(maxStreak, currentStreak);
                lastTimestamp = point.t;
            } else {
                currentStreak = 0;
                lastTimestamp = null;
            }
        }
        
        return maxStreak * 30 / 60; // Convert to hours (assuming 30-second intervals)
    }

    // Sleep chart data
    const sleepChartData = {
        datasets: [
            {
                label: 'Sleep State',
                data: sleepScatterData,
                backgroundColor: '#9C27B0',
                hoverBackgroundColor: '#7B1FA2',
                pointRadius: 3,
                pointHoverRadius: 5,
                pointBorderColor: 'rgba(0,0,0,0.1)',
                pointHoverBorderWidth: 1,
                pointHoverBorderColor: '#333',
            }
        ]
    };

    // Visit chart data
    const visitChartData = {
        datasets: [
            {
                label: 'Secondary Presence',
                data: visitScatterData,
                backgroundColor: '#483C32',
                hoverBackgroundColor: '#483C32',
                pointRadius: 3,
                pointHoverRadius: 5,
                pointBorderColor: 'rgba(0,0,0,0.1)',
                pointHoverBorderWidth: 1,
                pointHoverBorderColor: '#333',
            }
        ]
    };

    return (
        <div>
            <div style={{ height: '200px', marginBottom: '20px' }}>
                <Scatter options={sleepChartOptions} data={sleepChartData} />
            </div>
            <div style={{ height: '200px', marginBottom: '20px' }}>
                <Scatter options={visitChartOptions} data={visitChartData} />
            </div>
            {/* {(sleepStats || visitStats) && (
                <div style={{ marginTop: '10px', fontSize: '13px', color: '#555' }}>
                    {sleepStats && (
                        <div style={{ display: 'inline-block', marginRight: '20px' }}>
                            <span style={{ color: '#9C27B0', fontWeight: 'bold' }}>Sleep: </span>
                            {sleepStats.sleepPercentage}% of time | 
                            Longest duration: ~{sleepStats.longestSleepSegment.toFixed(1)}h | 
                            {sleepStats.sleepTransitions} transitions
                        </div>
                    )}
                    {visitStats && (
                        <div style={{ display: 'inline-block' }}>
                            <span style={{ color: '#FF9800', fontWeight: 'bold' }}>Visit: </span>
                            {visitStats.presencePercentage}% detected | 
                            Longest duration: ~{visitStats.longestPresenceSegment.toFixed(1)}h
                        </div>
                    )}
                </div>
            )} */}
        </div>
    );
};

export default PresenceChart;
